// Las dos piezas de datos de la ficha: los tres KPIs de arriba (cajas, palets,
// bruto, como en el diseño que pasó Víctor) y el bloque plegable «Detalles» con
// fechas, transporte, pesos y back orders.
//
// Los huecos se respetan: peso o cajas `null` son «—» (no 0), la fecha de
// finalizado sale siempre «—» con su tooltip mientras la columna de Fabric siga
// pendiente, y un palet sin dato no se inventa.
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useModulos } from '@/hooks/useModulos'
import type { CargaSalida } from '@/types'
import {
  fechaDe, lineasPalets, lineasPesoExpedicion, modoTransporteDe, pesoExpedicionMandante,
} from '@/utils/cargasSalida'
import { fmtCajas, fmtDate, fmtFechaHora, fmtPeso } from '../formatoSalida'

function Tile({ dark, etiqueta, children, sub }: {
  dark: boolean; etiqueta: string; children: ReactNode; sub?: ReactNode
}) {
  return (
    <div className={`rounded-lg px-3 py-2 min-w-0 ${dark ? 'bg-white/[0.05]' : 'bg-slate-100'}`}>
      <p className={`text-[11px] ${dark ? 'text-slate-400' : 'text-slate-600'}`}>{etiqueta}</p>
      <p className={`text-[16px] font-bold tabular-nums leading-tight ${dark ? 'text-slate-100' : 'text-slate-900'}`}>{children}</p>
      {sub && <div className={`mt-0.5 text-[10px] tabular-nums ${dark ? 'text-slate-400' : 'text-slate-600'}`}>{sub}</div>}
    </div>
  )
}

/** Cajas, palets y bruto. La cifra de palets es la que manda (la real, o la
 *  MONO si es estimada); debajo van las demás lecturas, como en la celda. */
export function FichaKpis({ c, dark }: { c: CargaSalida; dark: boolean }) {
  const { t } = useTranslation()
  const palets = lineasPalets(c)
  const real = palets.find(l => l.clave === 'real')
  return (
    <div className="grid grid-cols-3 gap-2">
      <Tile dark={dark} etiqueta={t('cargasSalida.tabla.columnas.cajas')}>{fmtCajas(c.cajas)}</Tile>
      <Tile
        dark={dark}
        etiqueta={t('cargasSalida.tabla.columnas.palets')}
        sub={palets.filter(l => l !== real).map(l => (
          <div key={l.clave}>{t(`cargasSalida.ficha.fases.est.${l.clave}`, { n: l.valor })}</div>
        ))}
      >
        {real
          ? t('cargasSalida.ficha.fases.paletsReales', { count: real.valor })
          : (c.palets ?? '—')}
      </Tile>
      <Tile
        dark={dark}
        etiqueta={t('salesOrders.picking.brutoCorto')}
        sub={c.pesoNeto != null && `${t('salesOrders.picking.neto')} ${fmtPeso(c.pesoNeto)}`}
      >
        {fmtPeso(c.pesoBruto)}
      </Tile>
    </div>
  )
}

function Dato({ dark, etiqueta, children, title }: {
  dark: boolean; etiqueta: string; children: ReactNode; title?: string
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1" title={title}>
      <dt className={`text-[11px] ${dark ? 'text-slate-400' : 'text-slate-600'}`}>{etiqueta}</dt>
      <dd className={`text-[12px] font-semibold text-right min-w-0 truncate ${dark ? 'text-slate-100' : 'text-slate-800'}`}>{children}</dd>
    </div>
  )
}

/** Un bloque plegable de la ficha. Nativo (`<details>`): teclado y lector de
 *  pantalla de serie, y sin estado que mantener. */
export function Plegable({ dark, titulo, abierto, onToggle, children }: {
  dark: boolean; titulo: string; abierto?: boolean; onToggle?: (abierto: boolean) => void; children: ReactNode
}) {
  return (
    <details
      open={abierto}
      onToggle={e => onToggle?.((e.currentTarget as HTMLDetailsElement).open)}
      className={`group rounded-lg border ${dark ? 'border-white/8' : 'border-slate-200'}`}
    >
      <summary className={`flex items-center justify-between cursor-pointer select-none px-3 py-2 text-[11px] font-bold uppercase tracking-wider list-none ${
        dark ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-800'
      }`}>
        {titulo}
        <svg className="h-3.5 w-3.5 transition-transform group-open:rotate-180" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M5 8l5 5 5-5" />
        </svg>
      </summary>
      <div className="px-3 pb-3">{children}</div>
    </details>
  )
}

interface DetallesProps {
  c: CargaSalida
  dark: boolean
  /** Lleva a la pestaña «Back orders» de la pantalla (y cierra la ficha). */
  onVerBackOrders: () => void
}

export function FichaDetalles({ c, dark, onVerBackOrders }: DetallesProps) {
  const { t } = useTranslation()
  const { puede } = useModulos()
  const pesoExp = lineasPesoExpedicion(c)
  const pesoExpMandante = pesoExpedicionMandante(c)
  const modo = modoTransporteDe(c)
  const bos = c.backOrders ?? 0
  const vacio = <span className={dark ? 'text-slate-500' : 'text-slate-400'}>—</span>

  return (
    <Plegable dark={dark} titulo={t('cargasSalida.ficha.detalles')}>
      <dl>
        <Dato dark={dark} etiqueta={t('cargasSalida.ficha.fechas.servicio')}>
          {fechaDe(c.fechaEnvioPrevista) ? fmtDate(c.fechaEnvioPrevista ?? undefined) : vacio}
        </Dato>
        <Dato
          dark={dark}
          etiqueta={t('cargasSalida.ficha.fechas.reservaCompleta')}
          title={c.fechaReservaCompleta ? undefined : t('cargasSalida.ficha.fechas.sinReservaCompleta')}
        >
          {c.fechaReservaCompleta ? fmtFechaHora(c.fechaReservaCompleta) : vacio}
        </Dato>
        <Dato
          dark={dark}
          etiqueta={t('cargasSalida.ficha.fechas.finalizado')}
          title={c.fechaFinalizado ? undefined : t('cargasSalida.ficha.fechas.sinFinalizar')}
        >
          {c.fechaFinalizado ? fmtFechaHora(c.fechaFinalizado) : vacio}
        </Dato>
        <Dato dark={dark} etiqueta={t('cargasSalida.ficha.fechas.transporteConfirmado')}>
          {c.fechaTransporteConfirmada ? fmtFechaHora(c.fechaTransporteConfirmada) : vacio}
        </Dato>
        <Dato dark={dark} etiqueta={t('cargasSalida.tabla.columnas.incoterm')}>{c.incoterm || vacio}</Dato>
        <Dato dark={dark} etiqueta={t('cargasSalida.tabla.columnas.transportista')}>{c.transportista || vacio}</Dato>
        <Dato dark={dark} etiqueta={t('cargasSalida.tabla.columnas.grupoCliente')}>{c.grupoCliente || vacio}</Dato>
        <Dato dark={dark} etiqueta={t('cargasSalida.tabla.columnas.transporte')}>
          {modo ? t(`cargasSalida.tabla.modos.${modo}`, { defaultValue: modo }) : vacio}
        </Dato>
        <Dato dark={dark} etiqueta={t('cargasSalida.tabla.columnas.muelle')}>
          <span className="inline-flex items-center gap-2">
            {c.muelle ? `${c.muelle}${c.camion ? ` · ${c.camion}` : ''}` : t('cargasSalida.muelle.sinMuelle')}
            {/* El cuadro de muelles sólo se ofrece a quien abre ese módulo: el
                enlace a una pantalla que daría 403 es peor que no ponerlo. */}
            {puede('muelles') && (
              <Link to="/muelles" className="text-[11px] font-semibold text-sky-600 hover:text-sky-500 hover:underline">
                {t('cargasSalida.ficha.transporte.verMuelles')}
              </Link>
            )}
          </span>
        </Dato>
        <Dato dark={dark} etiqueta={t('cargasSalida.tabla.columnas.pesoExpedicion')}>
          {fmtPeso(pesoExpMandante)}
        </Dato>
        {pesoExp.length > 1 && pesoExp.map(l => (
          <Dato key={l.clave} dark={dark} etiqueta={`· ${t(`cargasSalida.tabla.pesoExpedicion.${l.clave}`)}`}>
            {fmtPeso(l.valor)}
          </Dato>
        ))}
        <Dato dark={dark} etiqueta={t('cargasSalida.ficha.backOrders.titulo')}>
          {bos === 0 ? (
            <span className={`font-normal ${dark ? 'text-slate-400' : 'text-slate-600'}`}>{t('cargasSalida.ficha.backOrders.ninguno')}</span>
          ) : (
            <span className="inline-flex items-center gap-2">
              <span className={dark ? 'text-amber-300' : 'text-amber-700'}>{t('cargasSalida.ficha.backOrders.lineas', { count: bos })}</span>
              <button
                type="button"
                onClick={onVerBackOrders}
                className="text-[11px] font-semibold text-sky-600 hover:text-sky-500 hover:underline cursor-pointer"
              >
                {t('cargasSalida.ficha.backOrders.ver')}
              </button>
            </span>
          )}
        </Dato>
      </dl>
    </Plegable>
  )
}
